import type {
  CampaignIntent,
  DisplayMarketingAd,
  DisplayMarketingAdSet,
} from "common/interfaces/KYDMarketing";
import { AdFormat, RatioType } from "common/interfaces/KYDMarketing";
import { KYDAdminCampaign } from "common/interfaces/KYDEvent";

export function getMetric(
  metrics: KYDAdminCampaign | undefined,
  key: keyof KYDAdminCampaign
): string {
  if (!metrics) return "—";
  const v = metrics[key];
  if (v == null) return "—";
  return String(v);
}

/** Comma-separated custom audience names only; null if none to show. */
export function formatAudience(adset: DisplayMarketingAdSet): string | null {
  const audiences = adset.targeting?.custom_audiences;
  if (!audiences?.length) return null;
  const names = audiences
    .map((a) => (typeof a.name === "string" ? a.name.trim() : ""))
    .filter((n) => n.length > 0);
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * When there are no custom audiences, returns a fallback targeting row (e.g. Geo/DMA, Comscore,
 * interests, demographics) so we still show what drives targeting. Returns null if nothing to show.
 */
export function getTargetingFallback(
  adset: DisplayMarketingAdSet
): { label: string; value: string } | null {
  const t = adset.targeting;
  if (!t) return null;

  // Geo / DMA / Comscore (geo_markets often contain DMA or COMSCORE_MARKET entries)
  const geoMarkets = t.locations?.geo_markets;
  if (geoMarkets?.length) {
    const value = geoMarkets
      .map((g) => g.value ?? g.key)
      .filter(Boolean)
      .join(", ");
    if (value) return { label: "Geo / DMA", value };
  }

  // Interests: only show when we have a name (value); never show raw IDs
  const interests = t.interests;
  if (interests?.length) {
    const value = interests
      .map((i) => i.value?.trim())
      .filter((name): name is string => Boolean(name))
      .join(", ");
    if (value) return { label: "Interests", value };
  }

  // Demographics (age and/or gender)
  const demo = t.demographics;
  const ageMin = demo?.age_min;
  const ageMax = demo?.age_max;
  const genders = demo?.genders;
  const hasAge =
    (ageMin != null && ageMin > 0) || (ageMax != null && ageMax < 100);
  const hasGender = Array.isArray(genders) && genders.length > 0;
  if (hasAge || hasGender) {
    const parts: string[] = [];
    if (hasAge) {
      const min = ageMin ?? 18;
      const max = ageMax ?? 65;
      parts.push(`Age ${min}–${max}`);
    }
    if (hasGender && genders!.length) {
      parts.push(genders!.join(", "));
    }
    if (parts.length)
      return { label: "Demographics", value: parts.join(" • ") };
  }

  return null;
}

export function formatLocation(adset: DisplayMarketingAdSet): string {
  const loc = adset.targeting?.locations;
  if (!loc) return "—";
  const parts: string[] = [];
  if (loc.geo_markets?.length) {
    parts.push(loc.geo_markets.map((g) => g.value ?? g.key).join(", "));
  }
  if (loc.cities?.length) {
    parts.push(loc.cities.map((c) => c.value ?? c.key).join(", "));
  }
  if (loc.regions?.length) {
    parts.push(loc.regions.map((r) => r.value ?? r.key).join(", "));
  }
  if (loc.countries?.length) {
    parts.push(loc.countries.map((c) => c.value ?? c.key).join(", "));
  }
  return parts.length ? parts.join(" • ") : "—";
}

export type InsightEntityType = "ad" | "adset";

/** One insight item: line + variant for rendering in the metrics card. */
export interface InsightItem {
  line: string;
  variant: InsightVariant;
  /** Full explanation for tooltip on hover. */
  fullExplanation?: string;
  /** Intent this item refers to (for Apply/Ignore actions). */
  intent: CampaignIntent;
}

/** Variant from intent type and entity type (ad vs adset have different intent types). */
function getInsightVariantFromIntentType(
  intentType: CampaignIntent["intent_type"],
  entityType: InsightEntityType
): InsightVariant | null {
  if (entityType === "adset") {
    if (intentType === "INCREASE_BUDGET") return "positive";
    if (
      intentType === "PAUSE_ADSET" ||
      intentType === "PAUSE_CAMPAIGN" ||
      intentType === "CONSOLIDATE_ADSETS"
    )
      return "negative";
    return null;
  }
  if (intentType === "PAUSE_AD" || intentType === "INFORM_CREATIVE_FATIGUE")
    return "negative";
  return null;
}

/**
 * Unified helper: returns insight items for an ad or adset.
 * Pass entityType and the entity (ad or adset) plus intents already filtered for that entity.
 */
export function getInsightItems(options: {
  entityType: InsightEntityType;
  entity: DisplayMarketingAdSet | DisplayMarketingAd;
  intents: CampaignIntent[];
}): InsightItem[] {
  const { entityType, entity, intents } = options;
  const items: InsightItem[] = [];
  for (const intent of intents) {
    const summary =
      intent.explanation?.short_summary ??
      intent.explanation?.summary ??
      intent.reason;
    const variant: InsightVariant | null =
      intent.actionable === false
        ? "neutral"
        : getInsightVariantFromIntentType(intent.intent_type, entityType);
    if (variant !== null) {
      // Only the top list of intents (Performance Insights) shows entity names; in the card we show just the summary.
      const line = summary ?? "";
      const fullExplanation =
        intent.explanation?.summary ?? intent.reason ?? "";
      items.push({
        line,
        variant,
        fullExplanation: fullExplanation || undefined,
        intent,
      });
    }
  }
  return items;
}

/** Group ads into image (Poster) vs video creatives. */
export function groupAdsByCreativeType(
  ads: DisplayMarketingAd[]
): { type: "Poster" | "Video"; ads: DisplayMarketingAd[] }[] {
  const poster: DisplayMarketingAd[] = [];
  const video: DisplayMarketingAd[] = [];
  for (const ad of ads) {
    const format = ad.creative?.ad_formats?.[0];
    const hasVideo = (ad.creative?.assets?.videos?.length ?? 0) > 0;
    if (format === AdFormat.SINGLE_VIDEO || hasVideo) {
      video.push(ad);
    } else {
      poster.push(ad);
    }
  }
  const result: { type: "Poster" | "Video"; ads: DisplayMarketingAd[] }[] = [];
  if (poster.length) result.push({ type: "Poster", ads: poster });
  if (video.length) result.push({ type: "Video", ads: video });
  return result.length ? result : [{ type: "Poster", ads }];
}

export function formatRoas(value: string): string {
  if (value === "—" || value === "") return value;
  const n = parseFloat(value);
  if (Number.isNaN(n)) return value;
  return `${n.toFixed(2)}x`;
}

/** Display label for aspect ratio (e.g. "Square 1:1", "Vertical 16:9"). */
export function ratioLabel(type?: RatioType): string {
  return type === RatioType.square ? "Square 1:1" : "Vertical 16:9";
}

export type InsightVariant = "positive" | "negative" | "neutral";

/** Chakra color tokens for insight variant (e.g. performance badges, trend icons). */
export function getInsightVariantColors(variant: InsightVariant): {
  color: string;
  bg: string;
} {
  if (variant === "positive") return { color: "green.600", bg: "green.50" };
  if (variant === "negative") return { color: "red.600", bg: "red.50" };
  return { color: "gray.600", bg: "gray.50" };
}

/** Action label for the primary insight link (pass entityType for "Pause Ad" vs "Pause Ad Set"). */
export function getInsightActionLabel(
  variant: InsightVariant,
  entityType: InsightEntityType = "adset"
): string {
  if (variant === "positive") return "Increase Budget";
  if (variant === "neutral") return "View";
  return entityType === "ad" ? "Pause Ad" : "Pause Ad Set";
}
